// modify-limit planner. Three responsibilities:
//   1. Replace an existing limit clause's bytes with a new clause that
//      preserves the existing dialect-specific style (TOP / LIMIT … OFFSET /
//      OFFSET … FETCH / FETCH FIRST).
//   2. Synthesize a new limit clause when none exists, using the dialect from
//      `ParseOptions.engine`. Insert it past the last existing clause body
//      (ORDER BY → HAVING → GROUP BY → … → SELECT) and before the next clause
//      boundary keyword.
//   3. Drop the existing clause entirely when both `count` and `offset` are
//      undefined.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — modify.

import type { SqlEngine } from '../../sql-grammar/model';
import { splitSelectClauses } from '../structure/selectShape';
import type { LimitSpec, PlannedByteEdit, StructuralEdit } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import {
  CLAUSE_BOUNDARIES_AFTER_ORDER_BY,
  deleteEdit,
  findClauseInsertionPoint,
  insertEdit,
  pickClauseInsertOffset,
  scanStatementKeywords,
} from './applyStructuralEdit.shared';

type LimitParts = { readonly count?: string; readonly offset?: string };

export function planLimitEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-limit' }>,
): readonly PlannedByteEdit[] {
  const parts: LimitParts = {
    ...(edit.count !== undefined ? { count: edit.count } : {}),
    ...(edit.offset !== undefined ? { offset: edit.offset } : {}),
  };
  const isEmpty = parts.count === undefined && parts.offset === undefined;
  const lim = ctx.structure.limit;
  if (lim) return planReplaceExisting(ctx, lim, parts, isEmpty);
  if (isEmpty) return [];
  return planSynthesize(ctx, parts);
}

function planReplaceExisting(
  ctx: EditCtx,
  lim: LimitSpec,
  parts: LimitParts,
  isEmpty: boolean,
): readonly PlannedByteEdit[] {
  const existing = ctx.source.slice(lim.range.startOffset, lim.range.endOffset);
  const head = headWord(existing);
  if (isEmpty) return [planRemoveClause(ctx, lim, head)];
  const replacement = renderInExistingStyle(existing, head, parts);
  if (replacement === '') return [planRemoveClause(ctx, lim, head)];
  return [{
    range: lim.range,
    replacement,
    rationale: 'modify-limit:replace',
  }];
}

function planRemoveClause(
  ctx: EditCtx,
  lim: LimitSpec,
  head: string,
): PlannedByteEdit {
  const stmtStart = ctx.statementInfo.range.startOffset;
  let start = lim.range.startOffset;
  let end = lim.range.endOffset;
  // TOP sits mid-statement (between SELECT and the select-list), so we trim
  // forward to consume its trailing whitespace. Every other limit form lives
  // at the tail of the statement — trim leading whitespace instead.
  if (head === 'TOP') {
    while (end < ctx.source.length && /\s/.test(ctx.source[end])) end++;
  } else {
    while (start > stmtStart && /\s/.test(ctx.source[start - 1])) start--;
  }
  return deleteEdit(ctx, start, end, 'modify-limit:remove');
}

function planSynthesize(
  ctx: EditCtx,
  parts: LimitParts,
): readonly PlannedByteEdit[] {
  // MSSQL count-only fresh add → `TOP (n)` in the SELECT keyword tail. TOP
  // needs no ORDER BY, so it's the row-limit form a "+ Add limit" affordance
  // can safely emit on a bare SELECT. An offset (TOP can't skip) or a missing
  // SELECT keyword falls through to the tail OFFSET…FETCH form below.
  if (ctx.options.engine === 'mssql' && parts.count !== undefined && parts.offset === undefined) {
    const topEdit = planSynthesizeMssqlTop(ctx, parts.count);
    if (topEdit) return [topEdit];
  }
  const text = renderFresh(ctx.options.engine, parts);
  if (text === '') return [];
  const after = limitSynthAnchor(ctx);
  const kws = scanStatementKeywords(ctx);
  const boundary = findClauseInsertionPoint(
    kws, after, CLAUSE_BOUNDARIES_AFTER_ORDER_BY, ctx.statementInfo.range.endOffset,
  );
  const at = pickClauseInsertOffset(
    ctx.source, after, boundary, ctx.statementInfo.range.startOffset,
  );
  return [insertEdit(ctx, at, ` ${text}`, 'modify-limit:synth')];
}

// Synthesize a fresh `TOP (n)` for MSSQL. The grammar is
// `SELECT [ALL | DISTINCT] [TOP (n)] <select-list>`, so TOP lands just AFTER
// the SELECT keyword — or after an ALL / DISTINCT qualifier when present,
// before the first select item. We reuse `splitSelectClauses` (the same
// splitter QueryStructure extraction uses) to find those keyword ranges rather
// than introducing a new tokenizer. Returns null when there's no SELECT
// keyword to anchor to (caller falls back to the OFFSET…FETCH tail form).
function planSynthesizeMssqlTop(ctx: EditCtx, count: string): PlannedByteEdit | null {
  const clauses = splitSelectClauses(
    ctx.source,
    ctx.statementInfo.range.startOffset,
    ctx.statementInfo.range.endOffset,
    ctx.options,
  );
  if (!clauses) return null;
  // Insert after the last-present of the SELECT-keyword prefix: an ALL /
  // DISTINCT qualifier if it exists, otherwise the SELECT keyword itself.
  const anchor = clauses.distinct?.end ?? clauses.all?.end ?? clauses.selectKw.end;
  return insertEdit(ctx, anchor, ` TOP (${count})`, 'modify-limit:synth-top');
}

function headWord(existing: string): string {
  const m = existing.match(/^\s*([A-Za-z_]+)/);
  return m ? m[1].toUpperCase() : '';
}

// eslint-disable-next-line complexity
function renderInExistingStyle(
  existing: string, head: string, parts: LimitParts,
): string {
  if (head === 'TOP') {
    return parts.count !== undefined ? `TOP ${parts.count}` : '';
  }
  if (head === 'OFFSET') {
    if (/FETCH/i.test(existing)) {
      return renderOffsetFetch(parts);
    }
    return parts.offset !== undefined ? `OFFSET ${parts.offset}` : '';
  }
  if (head === 'FETCH') {
    return parts.count !== undefined ? `FETCH FIRST ${parts.count} ROWS ONLY` : '';
  }
  return renderLimitOffset(parts);
}

function renderLimitOffset(parts: LimitParts): string {
  let out = '';
  if (parts.count !== undefined) out = `LIMIT ${parts.count}`;
  if (parts.offset !== undefined) out += out ? ` OFFSET ${parts.offset}` : `OFFSET ${parts.offset}`;
  return out;
}

function renderOffsetFetch(parts: LimitParts): string {
  const off = parts.offset ?? '0';
  if (parts.count === undefined) return `OFFSET ${off} ROWS`;
  return `OFFSET ${off} ROWS FETCH NEXT ${parts.count} ROWS ONLY`;
}

function renderFresh(engine: SqlEngine, parts: LimitParts): string {
  if (engine === 'oracle') {
    return parts.count !== undefined ? `FETCH FIRST ${parts.count} ROWS ONLY` : '';
  }
  if (engine === 'mssql') {
    // A count-only fresh add is handled earlier by planSynthesizeMssqlTop
    // (`TOP (n)` in the SELECT tail — no ORDER BY needed). This tail form is
    // reached only for the offset case (TOP can't skip) or the defensive
    // no-SELECT-keyword fallback; both pair with an ORDER BY.
    return renderOffsetFetch(parts);
  }
  return renderLimitOffset(parts);
}

// Anchor for synthesized limit text: end of the last clause-body that comes
// before the limit slot in standard order (ORDER BY → HAVING → GROUP BY →
// WHERE → JOINS → FROM → SELECT). The caller wraps the result with a leading
// space so the inserted clause is separated from its predecessor.
function limitSynthAnchor(ctx: EditCtx): number {
  const s = ctx.structure;
  if (s.orderBy?.length) {
    return s.orderBy[s.orderBy.length - 1].range.endOffset;
  }
  if (s.having?.length) {
    return s.having[s.having.length - 1].range.endOffset;
  }
  if (s.groupBy?.length) {
    return s.groupBy[s.groupBy.length - 1].range.endOffset;
  }
  if (s.where?.length) {
    return s.where[s.where.length - 1].range.endOffset;
  }
  if (s.joins?.length) {
    return s.joins[s.joins.length - 1].range.endOffset;
  }
  if (s.fromItems?.length) {
    return s.fromItems[s.fromItems.length - 1].range.endOffset;
  }
  if (s.selectItems?.length) {
    return s.selectItems[s.selectItems.length - 1].range.endOffset;
  }
  return ctx.statementInfo.range.endOffset;
}
